import { FC, useCallback, useEffect, useMemo, useState } from 'react';

// API
import apibridge from '../../../apibridge';

// REDUX
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';
import { useDispatch } from 'react-redux';

// PACKAGES
import { useSearchParams } from 'react-router-dom';
import { Accordion, Fade, Spinner, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Moment from 'moment';
import { useImmer } from 'use-immer';
import moment from 'moment';

// COMPONENTS
import TitleSection, { TitleSectionType } from '../../../components/TitleSection/TitleSection';
import SvgMask from '../../../components/_Helpers/SvgMask';
import DelayedFadeIn from '../../../components/_Helpers/DelayedFadeIn';
import GraphemeQuestion from '../../../components/Assessment/GraphemeQuestion';
import MarkingButton from '../../../components/Assessment/MarkingButton';
import StudentNavigationSpelling from './StudentNavigationSpelling';
import MoveAssessmentSpellingModal from './MoveAssessmentSpellingModal';

// UTILS
import { guid } from '../../../libs/utils';

// TYPES
import { AssessmentAnswerStatus, AssessmentEditLlassAssessmentInfo } from '../../../api/models';

import {
	DynamicFtux,
	FtuxStep,
	getRecordSpellingConfig,
	useFtuxHighlight,
	useDynamicFtuxRestart
} from '../../../components/Ftux';

const AssessmentSpelling: FC = () => {
	const dispatch = useDispatch();

	// Get Details from params
	const [searchParams] = useSearchParams();
	const recordId = searchParams.get('recordId') || '';
	const assessmentName = decodeURIComponent(searchParams.get('assessmentName') || '');

	// Loading State
	const [isLoading, setIsLoading] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	// State to hide/show control bar
	const [showConfirmationBar, setShowConfirmationBar] = useState(false);

	// Assessment Details
	const [assessmentSpellingDetailsData, setAssessmentSpellingDetailsData] = useImmer<AssessmentEditLlassAssessmentInfo>(
		{}
	);

	// Clone state of Assessment Details
	const [assessmentSpellingDetailsDataClone, setAssessmentSpellingDetailsDataClone] =
		useImmer<AssessmentEditLlassAssessmentInfo>({});

	// Get Details call
	const getAssessmentSpellingDetailsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAssessmentEditLlass(recordId);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentSpellingDetailsData(response.data.result);

				setAssessmentSpellingDetailsDataClone(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Details error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	const shouldHighlightRecordingDate = useFtuxHighlight('recording-date', 'record-spelling-ftux');
	const shouldHighlightStudent = useFtuxHighlight('student-view', 'record-spelling-ftux');
	const shouldHighlightGrapheneSpellingLetter = useFtuxHighlight('graphene-spelling-letter', 'record-spelling-ftux');
	const shouldHighlightStudentWordMarkingButtons = useFtuxHighlight(
		'spelling-word-marking-buttons',
		'record-spelling-ftux'
	);
	const shouldHighlightNotesView = useFtuxHighlight('notes-view', 'record-spelling-ftux');
	const shouldHighlightScore = useFtuxHighlight('score-view', 'record-spelling-ftux');
	const { restartTutorial, manualRestartTrigger } = useDynamicFtuxRestart('AssessmentsSpelling');

	const handleFtuxStepNext = useCallback((step: FtuxStep, stepIndex: number) => {
		// When clicking Next from #recording-date step, we're moving to #student-view
		// Open List A accordion at that point
		if (step.target === '#student-view') {
			const listAElement = document.querySelector('#spelling-list-A');
			const toggleButton = listAElement?.querySelector<HTMLButtonElement>('.accordion-button');

			if (toggleButton && toggleButton.getAttribute('aria-expanded') !== 'true') {
				toggleButton.click();
			}
		}
	}, []);

	const assessmentSpellingConfig = useMemo(() => {
		const config = getRecordSpellingConfig();
		return {
			...config,
			onStepNext: handleFtuxStepNext
		};
	}, [handleFtuxStepNext]);

	useEffect(() => {
		if (recordId) {
			getAssessmentSpellingDetailsData();
		}
	}, [recordId]); // eslint-disable-line react-hooks/exhaustive-deps

	const {
		firstName,
		lastName,
		className,
		listA,
		listB,
		marked,
		totalQuestions,
		semesterLabel,
		assessmentId,
		assessmentPeriodId,
		classId,
		studentId,
		type
	} = assessmentSpellingDetailsData || {};

	// State for recording date
	const [dateSelectedListA, setDateSelectedListA] = useState(listA?.recordingDate || new Date().toDateString());
	const [dateSelectedListB, setDateSelectedListB] = useState(listB?.recordingDate || new Date().toDateString());

	useEffect(() => {
		if (listA?.recordingDate) {
			setDateSelectedListA(listA?.recordingDate);
		}
	}, [listA?.recordingDate]);

	useEffect(() => {
		if (listB?.recordingDate) {
			setDateSelectedListB(listB?.recordingDate);
		}
	}, [listB?.recordingDate]);

	// Title Props
	const titleSectionProps: TitleSectionType = {
		smallTitle: 'LLASS',
		smallSubTitle: assessmentName,
		secondaryTitle: semesterLabel,
		backToText: 'Back',
		backToUrl: '/assessment-management?tabView=Results'
	};

	// EDIT Call
	const confirmEditSpellingAssessment = async () => {
		setIsEditing(true);

		const submitData = {
			recordId,
			listAAnswers: assessmentSpellingDetailsData?.listA?.questions?.map((questionItem) => ({
				id: questionItem?.id,
				incorrectNote: questionItem?.incorrectNote,
				graphemes: questionItem?.graphemes?.map((graphemeItem) => ({
					id: graphemeItem?.id,
					status: graphemeItem?.status
				}))
			})),
			listBAnswers: assessmentSpellingDetailsData?.listB?.questions?.map((questionItem) => ({
				id: questionItem?.id,
				incorrectNote: questionItem?.incorrectNote,
				graphemes: questionItem?.graphemes?.map((graphemeItem) => ({
					id: graphemeItem?.id,
					status: graphemeItem?.status
				}))
			})),
			listAComments: assessmentSpellingDetailsData?.listA?.comments,
			listBComments: assessmentSpellingDetailsData?.listB?.comments,
			listARecordingDate: dateSelectedListA,
			listBRecordingDate: dateSelectedListB
		};

		const response = await apibridge.postAssessmentEditLlass(submitData);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				// If successfull Edit , call the GET details again to update the state to the new data.
				const response = await apibridge.getAssessmentEditLlass(recordId);

				if (response && response.data) {
					if (!response.data.isError && response.data.result) {
						setAssessmentSpellingDetailsData(response.data.result);
					} else if (response.data.validationErrors) {
						for (const err of response.data.validationErrors) {
							dispatch(
								ToastMessagesSlice.actions.add({
									id: guid(),
									type: 'danger',
									heading: 'Assessment Details error',
									description: err.reason || 'Unknown error'
								})
							);
						}
					}
				}

				setShowConfirmationBar(false);

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Record saved.',
						description: ''
					})
				);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Submission error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsEditing(false);
	};

	// STUDENT NAVIGATION STATE
	const [showsStudentNavigationOffCanvas, setShowsStudentNavigationOffCanvas] = useState(false);

	// Function to discard changes (used by student navigation)
	const discardChanges = () => {
		setAssessmentSpellingDetailsData(assessmentSpellingDetailsDataClone);
		setDateSelectedListA(listA?.recordingDate || new Date().toDateString());
		setDateSelectedListB(listB?.recordingDate || new Date().toDateString());
		setShowConfirmationBar(false);

		dispatch(
			ToastMessagesSlice.actions.add({
				id: guid(),
				type: 'danger',
				heading: 'Changes to record discarded',
				description: ''
			})
		);
	};

	// MOVE ASSESSMENTS MODAL STATE
	const [showMoveAssessmentsModal, setShowMoveAssessmentsModal] = useState(false);

	return (
		<div className="assessment-page position-relative">
			<TitleSection {...titleSectionProps} isHelpSection={true} onHelpClick={() => restartTutorial()}>
				<div className="d-flex justify-content-between">
					<div className={`d-flex justify-content-start gap-3 ${isLoading ? 'opacity-20 pe-none' : ''}`}>
						<div
							id="student-view"
							className={`d-flex flex-column justify-content-center ${
								shouldHighlightStudent ? 'highlight-outline rounded-3' : ''
							}`}
						>
							<p className="mb-0 body-tiny fw-bold text-shades-500">Student</p>

							<div className="d-flex align-items-center gap-2">
								<p className="mb-0 body-small fw-semibold text-dark-text">
									{firstName} {lastName}
								</p>

								<button className="date-picker-display" onClick={() => setShowsStudentNavigationOffCanvas(true)}>
									<span className="body-small fw-bold text-vivid-blue">Change</span>
								</button>
							</div>
						</div>

						<div className="tab-divider"></div>

						<div className="d-flex flex-column justify-content-center">
							<p className="mb-0 body-tiny fw-bold text-shades-500">Class</p>

							<p className="mb-0 body-small fw-semibold text-dark-text">{className}</p>
						</div>

						<div className="tab-divider"></div>

						<div
							id="recording-date"
							className={`d-flex flex-row justify-content-center gap-3 ${
								shouldHighlightRecordingDate ? 'highlight-outline rounded-3' : ''
							}`}
						>
							<div className={`d-flex flex-column justify-content-center`}>
								<p className="mb-0 body-tiny fw-bold text-shades-500">List A Recording date</p>

								<button className="date-picker-display body-small fw-semibold text-dark-text d-flex justify-content-start gap-2">
									<span>{Moment(dateSelectedListA).format('DD/MM/YYYY')}</span>

									<span className="body-small fw-bold text-vivid-blue">Edit</span>

									<input
										className="date-picker"
										type="date"
										max={moment().format('YYYY-MM-DD')}
										value={dateSelectedListA}
										onChange={(e) => {
											setDateSelectedListA(e.target.value);

											setShowConfirmationBar(true);
										}}
									/>
								</button>
							</div>

							<div className="tab-divider"></div>

							<div className={`d-flex flex-column justify-content-center`}>
								<p className="mb-0 body-tiny fw-bold text-shades-500">List B Recording date</p>

								<button className="date-picker-display body-small fw-semibold text-dark-text d-flex justify-content-start gap-2">
									<span>{Moment(dateSelectedListB).format('DD/MM/YYYY')}</span>

									<span className="body-small fw-bold text-vivid-blue">Edit</span>

									<input
										className="date-picker"
										type="date"
										max={moment().format('YYYY-MM-DD')}
										value={dateSelectedListB}
										onChange={(e) => {
											setDateSelectedListB(e.target.value);

											setShowConfirmationBar(true);
										}}
									/>
								</button>
							</div>
						</div>
					</div>

					<div className="sort-container d-flex align-items-center">
						<div className="assessment-record-counter">
							{marked}/{totalQuestions} Marked
						</div>

						<Dropdown className="assessment-move-record-dropdown">
							<Dropdown.Toggle className="btn btn-sm btn-icon btn-dropdown btn-move-record w-auto">
								<SvgMask path="/svg/ellipsis-v.svg" width={24} height={24} />
							</Dropdown.Toggle>

							<Dropdown.Menu align="end">
								<Dropdown.Item
									as="button"
									className="move-record-dropdown-item"
									onClick={() => setShowMoveAssessmentsModal(true)}
								>
									Move record
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</div>
			</TitleSection>

			<div className="container">
				{isLoading ? (
					<div className="d-flex align-items-center justify-content-center position-relative w-100">
						<DelayedFadeIn>
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</DelayedFadeIn>
					</div>
				) : (
					<Accordion className="book-stage-accordion assessment-accordion gap-3-5 d-flex flex-column" alwaysOpen>
						{/* List A */}
						<Accordion.Item id="spelling-list-A" eventKey="spelling-list-A" className="stage-4">
							<Accordion.Header>
								<div className="stage-label btn-reset d-flex align-items-center justify-content-between w-100 gap-2-5 spelling-list-A">
									<h4 className="m-0">List A</h4>

									<div
										id={`score-view`}
										className={`py-3 px-2 mx-2 ${shouldHighlightScore ? 'highlight-outline rounded-3' : ''}`}
									>
										<h4 className="m-0 mx-3 score-text">
											Score {listA?.score !== undefined ? listA?.score : '-'}/{listA?.totalScore}
										</h4>
									</div>
								</div>
							</Accordion.Header>

							<Accordion.Body className="d-flex flex-column">
								{/* STAGE CONTENT */}
								{listA?.questions?.length &&
									listA?.questions.map((question, questionIndex) => {
										// Check to see if there is no Marked Grapheme
										const isThereMarkedGrapheme = question?.graphemes?.some(
											(graphemeItem) =>
												graphemeItem.status === AssessmentAnswerStatus.Successful ||
												graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
										);

										return (
											<div className="row assessment-accordion-row" key={question.id || questionIndex}>
												<div className="col-12 col-lg-8 d-flex flex-wrap gap-2 justify-content-between align-items-center border-right py-4 px-4">
													<div className="d-flex align-items-center flex-wrap gap-2">
														{question.graphemes?.length &&
															question.graphemes.map((graphemeItem, graphemeIndex) => {
																const isListAFirstGraphene = questionIndex === 0 && graphemeIndex === 0;
																const firstGrapheneToHighlight =
																	isListAFirstGraphene && shouldHighlightGrapheneSpellingLetter;
																return (
																	<GraphemeQuestion
																		id={isListAFirstGraphene ? 'graphene-spelling-letter' : undefined}
																		key={graphemeItem.id || graphemeIndex}
																		label={graphemeItem?.text || ''}
																		shouldHighlight={firstGrapheneToHighlight}
																		state={
																			graphemeItem.status === AssessmentAnswerStatus.Successful
																				? 'success'
																				: graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
																				? 'danger'
																				: 'default'
																		}
																		isSmallSize
																		onClick={() => {
																			setAssessmentSpellingDetailsData((draft) => {
																				const draftQuestion = draft.listA?.questions?.find(
																					(draftQuestion) => draftQuestion.id === question.id
																				);

																				const draftGrapheme = draftQuestion?.graphemes?.find(
																					(draftGraphemeItem) => draftGraphemeItem.id === graphemeItem.id
																				);

																				if (draftGrapheme && draftGrapheme.status !== undefined) {
																					if (
																						draftGrapheme.status === AssessmentAnswerStatus.Unsuccessful ||
																						draftGrapheme.status === AssessmentAnswerStatus.Pending
																					) {
																						draftGrapheme.status = AssessmentAnswerStatus.Successful;
																					} else {
																						draftGrapheme.status = AssessmentAnswerStatus.Unsuccessful;
																					}
																				}
																			});

																			setShowConfirmationBar(true);
																		}}
																	/>
																);
															})}
													</div>

													<div
														id={`${questionIndex === 0 ? 'spelling-word-marking-buttons' : undefined}`}
														className={`d-flex align-items-center gap-2 ms-auto ${
															questionIndex === 0 && shouldHighlightStudentWordMarkingButtons
																? 'highlight-outline rounded-3'
																: ''
														}`}
													>
														<MarkingButton
															state="incorrect"
															onClick={() => {
																setAssessmentSpellingDetailsData((draft) => {
																	const draftQuestion = draft.listA?.questions?.find(
																		(draftQuestion) => draftQuestion.id === question.id
																	);

																	draftQuestion?.graphemes?.forEach((graphemeItem) => {
																		graphemeItem.status = AssessmentAnswerStatus.Unsuccessful;
																	});
																});

																setShowConfirmationBar(true);
															}}
														/>

														<MarkingButton
															state="correct"
															onClick={() => {
																setAssessmentSpellingDetailsData((draft) => {
																	const draftQuestion = draft.listA?.questions?.find(
																		(draftQuestion) => draftQuestion.id === question.id
																	);

																	draftQuestion?.graphemes?.forEach((graphemeItem) => {
																		graphemeItem.status = AssessmentAnswerStatus.Successful;
																	});
																});

																setShowConfirmationBar(true);
															}}
														/>

														{/* Reset current questions Graphemes */}
														<OverlayTrigger
															placement="top"
															overlay={
																<Tooltip
																	id={`assessment-reset-btn-${question.id || questionIndex}`}
																	style={{ marginBottom: '1px' }}
																>
																	Reset
																</Tooltip>
															}
														>
															<button
																className="assessment-marking-reset-button"
																disabled={!isThereMarkedGrapheme}
																onClick={() => {
																	setAssessmentSpellingDetailsData((draft) => {
																		const draftQuestion = draft.listA?.questions?.find(
																			(draftQuestion) => draftQuestion.id === question.id
																		);

																		// Reset Graphemes
																		draftQuestion?.graphemes?.forEach((graphemeItem) => {
																			graphemeItem.status = AssessmentAnswerStatus.Pending;
																		});

																		// Reset incorrect Notes
																		if (draftQuestion) {
																			draftQuestion.incorrectNote = undefined;
																		}
																	});

																	setShowConfirmationBar(true);
																}}
															>
																<SvgMask path={'/svg/redo.svg'} className="icon" width={32} height={32} />
															</button>
														</OverlayTrigger>
													</div>
												</div>

												<div className="col-12 col-lg-4 d-flex flex-column align-items-start p-4">
													<div
														id={`${questionIndex === 0 ? 'notes-view' : undefined}`}
														className={`form-group w-100 ${
															questionIndex === 0 && shouldHighlightNotesView ? 'highlight-outline rounded-3' : ''
														}`}
													>
														<label
															htmlFor={`incorrect-note-${question.id}`}
															className="body-small fw-bold text-dark-text mb-1"
														>
															<strong>Incorrect response and note</strong>
														</label>

														<input
															value={question.incorrectNote ? question.incorrectNote : ''}
															className="form-control"
															id={`incorrect-note-${question.id}`}
															onChange={(e) => {
																setAssessmentSpellingDetailsData((draft) => {
																	const draftQuestion = draft.listA?.questions?.find(
																		(draftQuestion) => draftQuestion.id === question.id
																	);

																	if (draftQuestion) {
																		draftQuestion.incorrectNote = e.target.value;
																	}
																});

																setShowConfirmationBar(true);
															}}
														/>
													</div>
												</div>
											</div>
										);
									})}

								{/* COMMENT */}
								<div className="row assessment-accordion-row">
									<div className="col-12">
										<div className="d-flex flex-column align-align-items-start py-3 px-3">
											<label htmlFor="questionA-comment" className="body-small fw-bold text-dark-text mb-1">
												<strong>Comments</strong>
											</label>

											<textarea
												id="questionA-comment"
												value={listA?.comments}
												onChange={(e) => {
													setAssessmentSpellingDetailsData((draft) => {
														if (draft.listA) {
															draft.listA.comments = e.target.value;
														}
													});

													setShowConfirmationBar(true);
												}}
												placeholder="Add a comment or note about the assessment of this list"
											></textarea>
										</div>
									</div>
								</div>
							</Accordion.Body>
						</Accordion.Item>

						{/* List B */}
						<Accordion.Item id="spelling-list-B" eventKey="spelling-list-B" className="stage-4">
							<Accordion.Header>
								<div className="stage-label btn-reset d-flex align-items-center justify-content-between w-100 gap-2-5 spelling-list-B">
									<h4 className="m-0">List B</h4>

									<h4 className="m-0 mx-3 score-text">
										Score {listB?.score !== undefined ? listB?.score : '-'}/{listB?.totalScore}
									</h4>
								</div>
							</Accordion.Header>

							<Accordion.Body className="d-flex flex-column">
								{/* STAGE CONTENT */}
								{listB?.questions?.length &&
									listB?.questions.map((question, questionIndex) => {
										// Check to see if there is no Marked Grapheme
										const isThereMarkedGrapheme = question?.graphemes?.some(
											(graphemeItem) =>
												graphemeItem.status === AssessmentAnswerStatus.Successful ||
												graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
										);

										return (
											<div className="row assessment-accordion-row" key={question.id || questionIndex}>
												<div className="col-8 d-flex flex-wrap gap-2 justify-content-between align-items-center border-right py-4 px-4">
													<div className="d-flex align-items-center flex-wrap gap-2">
														{question.graphemes?.length &&
															question.graphemes.map((graphemeItem, graphemeIndex) => {
																return (
																	<GraphemeQuestion
																		key={graphemeItem.id || graphemeIndex}
																		label={graphemeItem?.text || ''}
																		state={
																			graphemeItem.status === AssessmentAnswerStatus.Successful
																				? 'success'
																				: graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
																				? 'danger'
																				: 'default'
																		}
																		isSmallSize
																		onClick={() => {
																			setAssessmentSpellingDetailsData((draft) => {
																				const draftQuestion = draft.listB?.questions?.find(
																					(draftQuestion) => draftQuestion.id === question.id
																				);

																				const draftGrapheme = draftQuestion?.graphemes?.find(
																					(draftGraphemeItem) => draftGraphemeItem.id === graphemeItem.id
																				);

																				if (draftGrapheme && draftGrapheme.status !== undefined) {
																					if (
																						draftGrapheme.status === AssessmentAnswerStatus.Unsuccessful ||
																						draftGrapheme.status === AssessmentAnswerStatus.Pending
																					) {
																						draftGrapheme.status = AssessmentAnswerStatus.Successful;
																					} else {
																						draftGrapheme.status = AssessmentAnswerStatus.Unsuccessful;
																					}
																				}
																			});

																			setShowConfirmationBar(true);
																		}}
																	/>
																);
															})}
													</div>

													<div className="d-flex align-items-center gap-2 ms-auto">
														<MarkingButton
															state="incorrect"
															onClick={() => {
																setAssessmentSpellingDetailsData((draft) => {
																	const draftQuestion = draft.listB?.questions?.find(
																		(draftQuestion) => draftQuestion.id === question.id
																	);

																	draftQuestion?.graphemes?.forEach((graphemeItem) => {
																		graphemeItem.status = AssessmentAnswerStatus.Unsuccessful;
																	});
																});

																setShowConfirmationBar(true);
															}}
														/>

														<MarkingButton
															state="correct"
															onClick={() => {
																setAssessmentSpellingDetailsData((draft) => {
																	const draftQuestion = draft.listB?.questions?.find(
																		(draftQuestion) => draftQuestion.id === question.id
																	);

																	draftQuestion?.graphemes?.forEach((graphemeItem) => {
																		graphemeItem.status = AssessmentAnswerStatus.Successful;
																	});
																});

																setShowConfirmationBar(true);
															}}
														/>

														{/* Reset current questions Graphemes */}
														<OverlayTrigger
															placement="top"
															overlay={
																<Tooltip
																	id={`assessment-reset-btn-${question.id || questionIndex}`}
																	style={{ marginBottom: '1px' }}
																>
																	Reset
																</Tooltip>
															}
														>
															<button
																className="assessment-marking-reset-button"
																disabled={!isThereMarkedGrapheme}
																onClick={() => {
																	setAssessmentSpellingDetailsData((draft) => {
																		const draftQuestion = draft.listB?.questions?.find(
																			(draftQuestion) => draftQuestion.id === question.id
																		);

																		// Reset Graphemes
																		draftQuestion?.graphemes?.forEach((graphemeItem) => {
																			graphemeItem.status = AssessmentAnswerStatus.Pending;
																		});

																		// Reset incorrect Notes
																		if (draftQuestion) {
																			draftQuestion.incorrectNote = undefined;
																		}
																	});

																	setShowConfirmationBar(true);
																}}
															>
																<SvgMask path={'/svg/redo.svg'} className="icon" width={32} height={32} />
															</button>
														</OverlayTrigger>
													</div>
												</div>

												<div className="col-4 d-flex flex-column align-align-items-start py-4 px-4">
													<div className="form-group w-100">
														<label
															htmlFor={`incorrect-note-${question.id}`}
															className="body-small fw-bold text-dark-text mb-1"
														>
															<strong>Incorrect response and note</strong>
														</label>

														<input
															value={question.incorrectNote ? question.incorrectNote : ''}
															className="form-control"
															id={`incorrect-note-${question.id}`}
															onChange={(e) => {
																setAssessmentSpellingDetailsData((draft) => {
																	const draftQuestion = draft.listB?.questions?.find(
																		(draftQuestion) => draftQuestion.id === question.id
																	);

																	if (draftQuestion) {
																		draftQuestion.incorrectNote = e.target.value;
																	}
																});

																setShowConfirmationBar(true);
															}}
														/>
													</div>
												</div>
											</div>
										);
									})}

								{/* COMMENT */}
								<div className="row assessment-accordion-row">
									<div className="col-12">
										<div className="d-flex flex-column align-align-items-start py-3 px-3">
											<label htmlFor="questionB-comment" className="body-small fw-bold text-dark-text mb-1">
												<strong>Comments</strong>
											</label>

											<textarea
												id="questionB-comment"
												value={listB?.comments}
												onChange={(e) => {
													setAssessmentSpellingDetailsData((draft) => {
														if (draft.listB) {
															draft.listB.comments = e.target.value;
														}
													});

													setShowConfirmationBar(true);
												}}
												placeholder="Add a comment or note about the assessment of this list"
											></textarea>
										</div>
									</div>
								</div>
							</Accordion.Body>
						</Accordion.Item>
					</Accordion>
				)}
			</div>

			<Fade in={showConfirmationBar} mountOnEnter unmountOnExit>
				<div className="assessment-confirmation-bar">
					<div className="container">
						<div className="assessment-confirmation-content">
							<p className="mb-0 fw-semibold">
								Unsaved changes have been made to this student record. Do you wish to save?
							</p>

							<div className="d-flex gap-2">
								<button onClick={() => confirmEditSpellingAssessment()} className="btn text-nowrap">
									{isEditing ? (
										<Spinner animation="border" size="sm" />
									) : (
										<>
											<SvgMask path={'/svg/save.svg'} width={16} height={16} />
											Save changes
										</>
									)}
								</button>

								<button
									onClick={() => {
										setAssessmentSpellingDetailsData(assessmentSpellingDetailsDataClone);

										setDateSelectedListA(listA?.recordingDate || new Date().toDateString());

										setDateSelectedListB(listB?.recordingDate || new Date().toDateString());

										setShowConfirmationBar(false);

										dispatch(
											ToastMessagesSlice.actions.add({
												id: guid(),
												type: 'danger',
												heading: 'Changes to record discarded',
												description: ''
											})
										);
									}}
									className="btn btn-white text-nowrap"
								>
									Discard changes
								</button>
							</div>
						</div>
					</div>
				</div>
			</Fade>

			{/* STUDENT NAVIGATION COMPONENT */}
			<StudentNavigationSpelling
				showOffCanvas={showsStudentNavigationOffCanvas}
				onHideOffCanvas={() => setShowsStudentNavigationOffCanvas(false)}
				showConfirmationBar={showConfirmationBar}
				onConfirmEditAssessment={confirmEditSpellingAssessment}
				onDiscardChanges={discardChanges}
				assessmentName={assessmentName}
				semesterLabel={semesterLabel}
				assessmentId={assessmentId}
				assessmentPeriodId={assessmentPeriodId}
				classId={classId}
				studentId={studentId}
				type={type}
			/>

			{/* MOVE ASSESSMENTS MODAL */}
			<MoveAssessmentSpellingModal
				show={showMoveAssessmentsModal}
				onHide={() => setShowMoveAssessmentsModal(false)}
				firstName={firstName}
				lastName={lastName}
				assessmentPeriodId={assessmentPeriodId}
				studentId={studentId}
				assessmentId={assessmentId}
				type={type}
				onMoveSuccess={getAssessmentSpellingDetailsData}
			/>

			<DynamicFtux
				config={assessmentSpellingConfig}
				tutorialType="AssessmentsSpelling"
				manualRestartTrigger={manualRestartTrigger}
			/>
		</div>
	);
};

export default AssessmentSpelling;
